// this data is used to contruct analytics data layer
import { Inject, Injectable } from '@angular/core';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { BRAND_NAME } from '@rd/core/tokens/rd-lib.token';
import { OrderItemStatus } from '@rd/types';
import { SiteMapRouteName, TrackData } from '@rd/types/analytics.type';
import { Sitemap } from '@rd/types/sitemap.type';

export interface AnzSitemap
  extends Pick<
    Sitemap,
    | 'homepage'
    | 'giftCard'
    | 'redeemCash'
    | 'transferConnect'
    | 'pointsActivity'
    | 'redemptions'
    | 'order'
    | 'everydaySpend'
  > {
  // custom pages for ANZ
  selectCard: string;
  faqs: string;
  privacyPolicy: string;
  contactUs: string;
  termsAndConditions: string;
  latestUpdates: string;
  notFound: string;
}

function normalizeBrand(brand: string): string {
  return brand.toLowerCase().replace(/\s/g, '-');
}

export const sitemap: AnzSitemap = {
  homepage: 'home',
  selectCard: 'select-card',
  // static pages
  faqs: 'faqs',
  privacyPolicy: 'privacy-policy',
  contactUs: 'contact-us',
  termsAndConditions: 'terms',
  latestUpdates: 'latest-updates',
  // features
  pointsActivity: 'points-statement',
  redemptions: 'my-redemptions',
  giftCard: {
    // ANZ only need to track full data events
    listingFullData: 'giftcard',
    checkoutFullData: {
      id: 'giftCard.checkoutFullData',
      getConfig: ({ brand }: { brand: string }) => {
        return `giftcard:${brand}:details`;
      },
    },
    reviewFullData: {
      id: 'giftCard.reviewFullData',
      getConfig: ({ brand }: { brand: string }) => {
        return `giftcard:${brand}:review`;
      },
    },
    successFullData: {
      id: 'giftCard.successFullData',
      getConfig: ({
        brand,
        status,
      }: {
        brand: string;
        status: OrderItemStatus;
      }) => {
        // expected status pending, failed, success
        const formattedStatus =
          status === OrderItemStatus.Fulfilled ? 'success' : status;
        return `giftcard:${brand}:${formattedStatus}`;
      },
    },
  },
  redeemCash: {
    // ANZ only need to track full data events
    checkoutFullData: 'redeemcash:details',
    reviewFullData: 'redeemcash:review',
    successFullData: {
      id: 'redeemcash.successFullData',
      getConfig: ({ status }: { status: OrderItemStatus }) => {
        // expected status pending, failed, success
        const formattedStatus =
          status === OrderItemStatus.Fulfilled ? 'success' : status;
        return `redeemcash:${formattedStatus}`;
      },
    },
  },
  transferConnect: {
    // ANZ only need to track full data event
    listingFullData: 'airline-partners',
    checkoutFullData: {
      id: 'transferConnect.checkoutFullData',
      getConfig: ({ brand }: { brand: string }) => {
        return `airline-partners:${brand}:details`;
      },
    },
    reviewFullData: {
      id: 'transferConnect.reviewFullData',
      getConfig: ({ brand }: { brand: string }) => {
        return `airline-partners:${brand}:review`;
      },
    },
    successFullData: {
      id: 'transferConnect.successFullData',
      getConfig: ({
        brand,
        status,
      }: {
        brand: string;
        status: OrderItemStatus;
      }) => {
        // expected status pending, failed, success
        const formattedStatus =
          status === OrderItemStatus.Fulfilled ? 'success' : status;
        return `airline-partners:${brand}:${formattedStatus}`;
      },
    },
  },
  everydaySpend: {
    checkoutFullData: {
      id: 'everydaySpend.checkoutFullData',
      getConfig: ({ brand }: { brand: string }) => {
        return `everyday-spend:${brand}:details`;
      },
    },
    reviewFullData: {
      id: 'everydaySpend.reviewFullData',
      getConfig: ({ brand }: { brand: string }) => {
        return `everyday-spend:${brand}:review`;
      },
    },
    successFullData: {
      id: 'everydaySpend.successFullData',
      getConfig: ({
        brand,
        status,
      }: {
        brand: string;
        status: OrderItemStatus;
      }) => {
        // expected status pending, failed, success
        const formattedStatus =
          status === OrderItemStatus.Fulfilled ? 'success' : status;
        return `everyday-spend:${brand}:${formattedStatus}`;
      },
    },
  },
  order: {
    manage: 'my-redemptions',
  },
  notFound: 'error',
};

@Injectable({
  providedIn: 'root',
})
export class AnzAnalyticsService extends AnalyticsService {
  protected isEnabled = true;
  sitemap = sitemap;

  constructor(@Inject(BRAND_NAME) private brandName: string) {
    super();
  }

  getRoutName(config: SiteMapRouteName): string {
    return '';
  }

  protected trackEvent(trackParams: TrackData): void {
    const { routeName, extra } = trackParams;
    // unconfigure route will be ignored
    if (!routeName) {
      return;
    }
    let pageName = '';

    if (typeof routeName === 'string') {
      pageName = routeName;
    } else if (
      // @ts-ignore
      extra.brand
    ) {
      pageName = routeName.getConfig({
        brand: normalizeBrand(
          // @ts-ignore
          extra.brand,
        ),
        // @ts-ignore
        status: extra.status,
      });
    } else {
      pageName = routeName.getConfig({
        // @ts-ignore
        status: extra.status,
      });
    }

    // put console.log here to verify the implementation
    // TODO: remove later
    console.log('ANZ track pageName', pageName, trackParams);
    // VSCode understand the override rd-lib/global.d.ts but ng-cli doesn't understand.
    // @ts-ignore
    window.digitalData = {
      version: '1.0',
      component: [],
      error: [],
      event: [],
      product: [],
      pageInstanceEnv: '',
      page: {
        // @ts-ignore
        pageInfo: {
          pageName,
          attributes: {
            platform: 'anz-rewards',
            tenant: this.brandName === 'ANZ' ? 'anzrewards' : 'businessrewards',
          },
        },
      },
      user: {},
    };

    // TODO: need update later
    // @ts-ignore
    if (window.fireViewEnd) {
      // @ts-ignore
      window.fireViewEnd();
    }
  }
}
